import { useState } from 'react';
import { useDraft } from '../../../draft/DraftContext';
import StagedControl from '../../../draft/components/StagedControl';
import StagedTransition from '../../../draft/components/StagedTransition';
import StagedValue from '../../../draft/components/StagedValue';
import StagedWas from '../../../draft/components/StagedWas';
import { useStagedSx } from '../../../draft/components/useStagedSx';
import StagedRowActions from '../../../draft/components/StagedRowActions';
import { stagedRowSx } from '../../../draft/components/stagedStyles';
import { useProposedColumn } from '../../../draft/coverageColumns';
import {
  coverageFieldPath,
  coverageInclusionPath,
  coverageRowPaths,
} from '../../../draft/stagedFields';
import { Box, Divider, Grid } from '@mui/material';
import { styled } from '@mui/styles';
import Color from 'color';
import {
  BasisOfCoverField,
  CheckBox,
  DeductibleField,
  formatCompactLimit,
  LimitField,
  RetroActiveField,
  TerritorialLimitField,
  WaitingPeriodField,
  type RetroActivePeriod,
} from '../../../../../shared/helpers';
import { Show } from '../../../../../../../../../components/Show';
import AmendatoryCoverageLineItemFactory from '../amendatory-coverages/AmendatoryCoverageLineItemFactory';
import { ContextTypes } from '../amendatory-coverages/manager';
import { CurrencyAmount } from '@cowbell/ui/core';
import { useLocale } from '../../../../../providers/LocaleProvider';
import { deriveCurrencyFormat } from '../../../../../../../../../i18n/currencies';
import UnderwriterSupplementalInfo from '../../../shared/UnderwriterSupplementalInfo';
import {
  useCommittedUnderwriteState,
  useUnderwriterContext,
} from '../../../provider/UnderwriterProvider';
import { useUiSettings } from '../../../../../providers/UiSettingsProvider';
import DeltaPricing from '../../../../../../../policies/prime-x/endorsements/components/coverages/DeltaPricing';
import { useCoverageStateHandlers } from '../amendatory-coverages/shared/useCoverageStateHandlers';
import { RaterXCoverage } from '../../../../../api/types/rater-x-preset';
import UnderwriterLineItemActions from '../../../shared/UnderwriterLineItemActions';
import CoverageFormPreview from '../../../shared/CoverageFormPreview';
import type { CoverageFormInfo } from '../../../../../api/types';
import type { Nullable } from '../../../../../../../../../types/common/utility';
import { QUOTE_ACTIONS } from '../../../../../../../primeX-underwriter/components/footer/constants';
import { useRegisterBlockingWarning } from '../../../hooks/useRegisterBlockingWarning';
import {
  getUnderwriteQuoteCurrency,
  isCoverageEndorsementsState,
  type ReferralStrategyForEdit,
} from '../../../types';
import type { Languages } from '../../../../../../../../../i18n/i18n.language-config';
import SubCoverageIndicator from '../../../../../shared/SubCoverageIndicator';
import { EndorsementEditAffordance } from '../../../inputs-factory/EndorsementEditAffordance';
import { useSchemaMap } from '../../../inputs-factory/SchemaMapContext';
import type { RaterRulesByField } from '../../../inputs-factory/types';
import {
  UW_INPUTS_INSET,
  UW_PANEL_CONTENT_END,
  UW_ROW_PADDING_X,
  UW_ROW_PADDING_Y,
} from '../../uwTileSpacing';
import { isMandatoryCoverage } from '../../../../../shared/coverageLock';

const BLOCKABLE_ACTIONS = [
  QUOTE_ACTIONS.RELEASE,
  QUOTE_ACTIONS.BIND,
  QUOTE_ACTIONS.UPDATE,
];

const CoverageLineItem = (props: CoverageLineItemProps) => {
  const locale = useLocale();
  const context = useUnderwriterContext();
  const coverageState = context.state.coverageState[props.id];
  useRegisterBlockingWarning(coverageState, BLOCKABLE_ACTIONS);
  const { uiSettings } = useUiSettings();
  const isNonStandardCoverage =
    (!coverageState.rules?.deductible && !coverageState.rules?.limit) ||
    !coverageState.value?.props?.limit?.value;
  const { handleCoverageSelectToggle, handleCoverageValuesUpdate } =
    useCoverageStateHandlers(props.id);

  // One currency for the whole row: the retention dropdown and the premium must not disagree.
  const rowCurrency = isCoverageEndorsementsState(context.state)
    ? context.state.policy.currency
    : getUnderwriteQuoteCurrency(context.state);

  const { stagedFields } = useDraft();
  const isStaged = stagedFields.at(coverageRowPaths(props.id)).length > 0;

  const schemaMap = useSchemaMap();
  const fieldKey = coverageState.field?.key;
  const hasInputsSchema = Boolean(fieldKey && schemaMap[fieldKey]);
  const [endorsementModalOpen, setEndorsementModalOpen] = useState(false);

  // Selecting a payload-bearing endorsement opens the inputs modal instead of
  // dispatching, so the rater isn't called until the user saves valid inputs.
  const handleSelectToggle = (isSelected: boolean) => {
    if (hasInputsSchema && isSelected && !coverageState.isSelected) {
      setEndorsementModalOpen(true);
      return;
    }
    handleCoverageSelectToggle(isSelected);
  };

  return (
    <CoverageItemContainer
      id={`coverage-line-item-${coverageState.id}`}
      container
      sx={{
        position: 'relative',
        // The whole row grounds when anything on it is staged, not just the
        // cell that changed. The row is the unit the underwriter accepts.
        ...(isStaged ? stagedRowSx() : {}),
      }}
    >
      <NameColumn
        id={props.id}
        coverageState={coverageState}
        isSubCoverage={props.isSubCoverage}
        coverageForm={props.coverageForm}
        isCoverageFormsLoading={props.isCoverageFormsLoading}
        productKey={context.state.productType}
        onCoverageSelectToggle={handleSelectToggle}
        onUpdatePegValue={(isPegValue) => {
          context.handlers.setCoverage({
            isPegValue,
            id: props.id,
          });
        }}
        onUpdateReferralStrategy={(referralStrategyForEdit) => {
          context.handlers.setCoverage({
            referralStrategyForEdit,
            id: props.id,
          });
        }}
      />

      <Show
        when={!isNonStandardCoverage}
        fallback={
          <AmendatoryCoverageLineItemFactory
            id={props.id}
            coverageState={coverageState}
            setCoverage={context.handlers.setCoverage}
            productType={context.state.productType}
            contextType={ContextTypes.UNDERWRITER}
          />
        }
      >
        <InputColumns
          id={props.id}
          coverageState={coverageState}
          uiSettings={uiSettings}
          onCoverageValuesUpdate={handleCoverageValuesUpdate}
          currency={rowCurrency}
        />
      </Show>

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        <EndorsementEditAffordance
          fieldKey={fieldKey}
          title={coverageState.fullName}
          isSelected={coverageState.isSelected}
          opened={endorsementModalOpen}
          existingProps={coverageState.value?.props}
          rules={coverageState.rules as RaterRulesByField | undefined}
          onOpenChange={setEndorsementModalOpen}
          onSave={(value) =>
            context.handlers.setCoverage({
              id: props.id,
              value: value as unknown as RaterXCoverage['value'],
              isSelected: true,
            })
          }
        />
      </Box>

      <ControlsColumn
        id={props.id}
        coverageState={coverageState}
        language={locale.language}
        currency={rowCurrency}
      />
    </CoverageItemContainer>
  );
};

type NameColumnProps = {
  /**
   * The STATE key, not `coverageState.id`. Multi-rater products file a coverage
   * under `${key}.${productType}` (`transformers/componentCoverages.ts`), and a
   * draft path addresses where the value lives — so a marker keyed on the
   * rater's own id would find nothing on exactly those products.
   */
  id: string;
  coverageState: RaterXCoverage;
  isSubCoverage?: boolean;
  readonly coverageForm?: Nullable<CoverageFormInfo>;
  readonly isCoverageFormsLoading?: boolean;
  readonly productKey: string;
  onCoverageSelectToggle: (isSelected: boolean) => void;
  onUpdatePegValue: (isPegValue: boolean) => void;
  onUpdateReferralStrategy: (
    referralStrategyForEdit: ReferralStrategyForEdit
  ) => void;
};

const NameColumn = ({
  id,
  coverageState,
  isSubCoverage,
  coverageForm,
  isCoverageFormsLoading,
  productKey,
  onCoverageSelectToggle,
  onUpdatePegValue,
  onUpdateReferralStrategy,
}: NameColumnProps) => {
  // Read, not restated: the header reads the same spans, and a literal here
  // left every column after the name one unit short of its heading.
  const spans = useProposedColumn();

  return (
    <Grid
      item
      xs={spans.name}
      sx={{
        paddingLeft: isSubCoverage ? '1.75rem' : '0.5rem',
        paddingTop: 0,
        // Not `hidden`: the staged inclusion mark hangs out of flow below the
        // label and would be clipped. The label truncates long names itself.
        overflow: 'visible',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        sx={{ flexWrap: 'nowrap', minWidth: 0 }}
      >
        {isSubCoverage && <SubCoverageIndicator />}
        {/* The anchor `StagedTransition` hangs from. */}
        <Box sx={{ minWidth: 0, position: 'relative' }}>
          <CheckBox
            isAvailable={coverageState.isAvailable}
            isSelected={coverageState.isSelected}
            label={coverageState.fullName}
            isMandatory={isMandatoryCoverage(coverageState)}
            tooltip={
              <CoverageTooltip coverageState={coverageState}>
                {coverageState.description}
              </CoverageTooltip>
            }
            onClick={() => onCoverageSelectToggle(!coverageState.isSelected)}
          />
          {/* A checkbox shows no new value, so the transition is written out. */}
          <StagedTransition paths={[coverageInclusionPath(id)]} />
        </Box>
        <CoverageFormPreview
          coverageForm={coverageForm ?? null}
          isLoading={Boolean(isCoverageFormsLoading)}
          productKey={productKey}
        />
        <UnderwriterLineItemActions
          isPegValue={coverageState.isPegValue}
          warnings={coverageState.warnings}
          errors={coverageState.validationErrors}
          referralReasons={coverageState.referralReasons}
          referralReasonsForEdit={coverageState.referralReasonsForEdit}
          referralStrategyForEdit={coverageState.referralStrategyForEdit}
          onUpdatePegValue={onUpdatePegValue}
          onUpdateReferralStrategy={onUpdateReferralStrategy}
        />
      </Box>
    </Grid>
  );
};

type InputColumnsProps = {
  /** The state key — see `NameColumnProps`. */
  id: string;
  coverageState: RaterXCoverage;
  uiSettings: ReturnType<typeof useUiSettings>['uiSettings'];
  onCoverageValuesUpdate: ReturnType<
    typeof useCoverageStateHandlers
  >['handleCoverageValuesUpdate'];
  /**
   * Same ISO 4217 code ControlsColumn renders the premium with, so the retention dropdown in this
   * row cannot show a different currency from the premium beside it (MS-417).
   */
  currency: string | null | undefined;
};

const InputColumns = ({
  id,
  coverageState,
  uiSettings,
  onCoverageValuesUpdate,
  currency,
}: InputColumnsProps) => {
  const spans = useProposedColumn();
  // The limit cell is its own `Grid item`, so it can carry the outline itself
  // and leave `LimitField`'s JSX untouched.
  const limitSx = useStagedSx([coverageFieldPath(id, 'limit')]);

  return (
    <>
      <Grid item xs={spans.limit} sx={limitSx}>
        {/* Both figures render in the slider's own value slot, so the struck
            one sits under the figure it replaces rather than under the track. */}
        <LimitField
          limits={(coverageState?.rules?.limit?.options ?? []) as number[]}
          limit={coverageState?.value?.props?.limit?.value as number}
          isAvailable={coverageState.isAvailable}
          isSelected={coverageState.isSelected}
          onChange={onCoverageValuesUpdate}
          renderValue={(formatted) => (
            <StagedValue
              paths={[coverageFieldPath(id, 'limit')]}
              formatted={formatted}
              // The slider's own formatter, so both figures share a notation.
              format={formatCompactLimit}
            />
          )}
        />
      </Grid>

      <Grid
        item
        xs={spans.inputs}
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: UW_INPUTS_INSET,
          // Centres the text against the bare-text cells beside it: 18px is
          // what MUI computes on the right, which it defends, so only the left
          // is set. Undoubling the selector loses to MUI and fails silently.
          '& .MuiSelect-select.MuiSelect-select': {
            paddingLeft: '18px',
          },
        }}
      >
        <Box display="flex" alignItems="center" width="100%">
          <Box width="100%" display="flex" justifyContent="center">
            <StagedControl paths={[coverageFieldPath(id, 'deductible')]}>
              <DeductibleField
                deductibles={
                  (coverageState.rules?.deductible?.options ?? []) as number[]
                }
                deductible={
                  coverageState.value?.props?.deductible?.value as number
                }
                isAvailable={coverageState.isAvailable}
                isSelected={coverageState.isSelected}
                onChange={onCoverageValuesUpdate}
                currency={currency}
              />
            </StagedControl>
          </Box>

          <Show when={uiSettings.showCoverageTerritorialLimit}>
            <Box width="100%" display="flex" justifyContent="center">
              <StagedControl
                paths={[coverageFieldPath(id, 'territorialLimit')]}
              >
                <TerritorialLimitField
                  territorialLimits={
                    (coverageState.rules?.territorialLimit?.options ??
                      []) as string[]
                  }
                  territorialLimit={
                    coverageState.value?.props?.territorialLimit
                      ?.value as string
                  }
                  isAvailable={coverageState.isAvailable}
                  isSelected={coverageState.isSelected}
                  onChange={onCoverageValuesUpdate}
                />
              </StagedControl>
            </Box>
          </Show>
          <Show when={uiSettings.showCoverageBasisOfCover}>
            <Box width="100%" display="flex" justifyContent="center">
              <StagedControl paths={[coverageFieldPath(id, 'basisOfCover')]}>
                <BasisOfCoverField
                  basisOfCovers={
                    (coverageState.rules?.basisOfCover?.options ??
                      []) as string[]
                  }
                  basisOfCover={
                    coverageState.value?.props?.basisOfCover?.value as string
                  }
                  isAvailable={coverageState.isAvailable}
                  isSelected={coverageState.isSelected}
                  onChange={onCoverageValuesUpdate}
                />
              </StagedControl>
            </Box>
          </Show>
          <Show when={uiSettings.showCoverageWaitingPeriods}>
            <Box width="100%" display="flex" justifyContent="center">
              <StagedControl paths={[coverageFieldPath(id, 'waitingPeriod')]}>
                <WaitingPeriodField
                  waitingPeriods={
                    (coverageState.rules?.waitingPeriod?.options ??
                      []) as number[]
                  }
                  waitingPeriod={
                    coverageState.value?.props?.waitingPeriod?.value as number
                  }
                  isAvailable={coverageState.isAvailable}
                  isSelected={coverageState.isSelected}
                  onChange={onCoverageValuesUpdate}
                />
              </StagedControl>
            </Box>
          </Show>

          <Show when={uiSettings.showCoverageRetroactiveDate}>
            <Box width="100%" display="flex" justifyContent="center">
              <StagedControl paths={[coverageFieldPath(id, 'retroactive')]}>
                <RetroActiveField
                  retroActivePeriods={
                    coverageState.rules?.retroactive?.retroPeriods ?? []
                  }
                  retroActivePeriod={
                    coverageState.value?.props?.retroactive
                      ?.props as RetroActivePeriod
                  }
                  isAvailable={coverageState.isAvailable}
                  isSelected={coverageState.isSelected}
                  onChange={onCoverageValuesUpdate}
                  isCustomDateAllowed={Boolean(
                    coverageState.rules?.retroactive?.isCustomDateAllowed
                  )}
                  maxDate={String(coverageState.rules?.retroactive?.to)}
                  customDateDefault={
                    coverageState.rules?.retroactive?.customDateDefault
                  }
                />
              </StagedControl>
            </Box>
          </Show>
        </Box>
      </Grid>
    </>
  );
};

/**
 * What this coverage would cost if the draft were accepted.
 *
 * An em dash while the rate is in flight or has failed. `formatCoveragePremium`
 * renders an absent figure as `N/A`, which beside a struck-through real premium
 * reads as "this coverage no longer has a price" rather than "we are still
 * asking" — and the banner already reports pricing and failure for the quote as
 * a whole.
 */
const ProposedPremium = ({
  draft,
  currency,
  language,
}: {
  readonly draft: number | undefined;
  readonly currency: string | null | undefined;
  readonly language: Languages;
}) => {
  if (draft === undefined) {
    return (
      <Box component="span" color="text.disabled">
        —
      </Box>
    );
  }

  return (
    <Box component="span" fontWeight={700}>
      {formatCoveragePremium(draft, currency, language)}
    </Box>
  );
};

type ControlsColumnProps = {
  /** The state key — see `NameColumnProps`. */
  readonly id: string;
  readonly coverageState: RaterXCoverage;
  readonly language: Languages;
  /**
   * ISO 4217 code for the policy/quote this coverage belongs to — the policy's currency when
   * editing an endorsement, the quote's own currency otherwise (see `getUnderwriteQuoteCurrency`).
   */
  readonly currency: string | null | undefined;
};

/**
 * Renders `premium` via the caller-supplied `currency` when one is available, falling back to
 * the locale-derived format only if that's genuinely unavailable — so this row never disagrees
 * with a sibling delta row on currency symbol.
 */
const formatCoveragePremium = (
  premium: number | null | undefined,
  currency: string | null | undefined,
  language: Languages
) =>
  currency != null ? (
    <CurrencyAmount amount={premium} currency={currency} placeholder="N/A" />
  ) : (
    deriveCurrencyFormat(language, premium, { placeholder: 'N/A' })
  );

/**
 * The premium cell. What this coverage costs on the committed quote, or the
 * endorsement delta in the MTE flow, which never stages.
 *
 * It used to stack the committed figure over the draft one when something was
 * staged. That was a workaround for a full grid with nowhere to put a second
 * number, and the Proposed column is what replaced it — so the stack is gone
 * rather than merely unreachable. Keeping it would have meant two cells racing
 * to show the same figure.
 */
const renderPremiumCell = ({
  coverageState,
  currency,
  language,
}: PremiumCellProps) => {
  if (coverageState.ratingDetails.premiumDelta) {
    return (
      <DeltaPricing
        price={coverageState.ratingDetails.premiumDelta.amount}
        currency={currency}
      />
    );
  }

  return formatCoveragePremium(
    coverageState.ratingDetails?.premium,
    currency,
    language
  );
};

type PremiumCellProps = {
  readonly coverageState: RaterXCoverage;
  readonly currency: string | null | undefined;
  readonly language: Languages;
};

const ControlsColumn = ({
  id,
  coverageState,
  language,
  currency,
}: ControlsColumnProps) => {
  const { rate, stagedFields } = useDraft();
  const spans = useProposedColumn();
  const rowPaths = coverageRowPaths(id);
  // Proposed is BLANK on a row nothing is staged on. A figure in every row
  // would say the whole grid is proposing something; the column is how the
  // underwriter finds what moved.
  const isStaged = stagedFields.at(rowPaths).length > 0;
  // `coverageState` is the SHOWN row: the draft with its re-rate laid over it,
  // so a coverage drafted as Excluded already reads premium 0 there. The
  // Premium cell is what the coverage costs today, which only the committed
  // quote knows (OMNI-1187). The MTE flow has no committed quote and never
  // stages, so it keeps the row it renders.
  const committedCoverage =
    useCommittedUnderwriteState()?.coverageState?.[id] ?? coverageState;
  // Only once the draft has actually priced. `premiumByCoverage` is empty when
  // nothing is staged, so with the flag off this is always undefined and the
  // Proposed column never renders at all.
  const draftPremium =
    rate.status === 'ok' && coverageState.key
      ? rate.premiumByCoverage[id]
      : undefined;

  return (
    <>
      <Grid item xs={spans.premium}>
        <Box
          width="100%"
          textAlign="right"
          sx={{ transform: 'translateX(-1.25rem)', pr: 1 }}
          // Struck on a staged row: this is what the coverage costs today, and
          // the cell beside it is proposing it should cost something else.
          color={isStaged ? 'text.disabled' : undefined}
          style={isStaged ? { textDecoration: 'line-through' } : undefined}
        >
          {renderPremiumCell({
            coverageState: committedCoverage,
            currency,
            language,
          })}
        </Box>
      </Grid>
      <Show when={spans.proposed > 0}>
        {/* Proposed holds the figure ALONE, so the heading above it names the
            number rather than the icons. */}
        <Grid item xs={spans.proposed}>
          <Box
            width="100%"
            textAlign="right"
            sx={{ transform: 'translateX(-1.25rem)' }}
          >
            <Show when={isStaged}>
              <ProposedPremium
                draft={draftPremium}
                currency={currency}
                language={language}
              />
            </Show>
          </Box>
        </Grid>
        {/* The unlabelled column past it. */}
        <Grid item xs={spans.actions}>
          {/* Right-aligned, as on the multi-rater row: the column is a
              twelfth of the grid and wider than the pair. It ends where the
              Category Premium totals end, not at the panel's edge. */}
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            pr={`calc(${UW_PANEL_CONTENT_END} - ${UW_ROW_PADDING_X})`}
          >
            <StagedRowActions paths={rowPaths} />
          </Box>
        </Grid>
      </Show>
    </>
  );
};

export default CoverageLineItem;

export const CoverageItemContainer = styled(Grid)(({ theme }) => {
  return {
    padding: `${UW_ROW_PADDING_Y} ${UW_ROW_PADDING_X}`,
    alignItems: 'center',
    position: 'relative',
    background:
      theme.palette.mode === 'light'
        ? theme.palette.background.modal
        : Color(theme.palette.background.modal).darken(0.15).toString(),

    '&:nth-child(odd)': {
      background: Color(theme.palette.background.default)
        .lighten(theme.palette.mode === 'dark' ? 0.28 : 0.025)
        .toString(),
    },
  };
});

type CoverageLineItemProps = {
  id: string;
  isSubCoverage?: boolean;
  /** Form backing this endorsement, when the category is amendatory (FIR-21152). */
  readonly coverageForm?: Nullable<CoverageFormInfo>;
  readonly isCoverageFormsLoading?: boolean;
};

const CoverageTooltip = ({
  children,
  coverageState,
}: {
  children?: React.ReactNode;
  coverageState: RaterXCoverage;
}) => {
  const coverageDescription = children
    ? 'Coverage description: '
    : 'No coverage description';
  return (
    <Box>
      <Box>
        <strong>{coverageDescription}</strong>
        {children}
      </Box>
      <Divider sx={{ mt: 1.5, mb: 0.5 }} />

      <Box>
        <strong>Applied guidelines and factors:</strong>
        <UnderwriterSupplementalInfo
          mt="0.2rem"
          ml="0.2rem"
          appliedRules={coverageState.appliedRules}
          factors={coverageState.ratingDetails?.factors}
          lostCost={coverageState.ratingDetails?.lostCost}
        />
      </Box>
    </Box>
  );
};
